import React, { useState, useEffect, useCallback, useRef } from 'react';
import { confirmar } from '../components/confirmacao';
import apiService from '../services/apiService';
import './KeytermsConfig.css';

const emptyForm = { id: null, term: '' };

function KeytermsConfig({ user }) {
    const [toast, setToast] = useState(null);
    const notify = useCallback((msg, type = 'info') => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 4000);
    }, []);

    const [companies, setCompanies] = useState([]);
    const [stores, setStores] = useState([]);
    const [subdomain, setSubdomain] = useState('');
    const [storeId, setStoreId] = useState('');   // '' = padrão da empresa (store_id NULL)

    const [items, setItems] = useState([]);        // termos do escopo selecionado
    const [defaults, setDefaults] = useState([]);   // termos padrão da empresa (informativo)
    const [loading, setLoading] = useState(false);

    const [formOpen, setFormOpen] = useState(false);
    const [form, setForm] = useState(emptyForm);
    const [saving, setSaving] = useState(false);
    const savingRef = useRef(false);   // guard síncrono contra double-submit

    // Empresas (admin)
    useEffect(() => {
        (async () => {
            try {
                const data = await apiService.promptEditorGetCompanies();
                setCompanies(Array.isArray(data) ? data : []);
            } catch (e) {
                notify('Erro ao carregar empresas', 'error');
            }
        })();
    }, [notify]);

    // Lojas ao trocar empresa
    useEffect(() => {
        setStores([]); setStoreId(''); setItems([]); setDefaults([]); setFormOpen(false);
        if (!subdomain) return;
        (async () => {
            try {
                const st = await apiService.promptEditorGetStores(subdomain);
                setStores(Array.isArray(st) ? st : []);
            } catch (e) { notify('Erro ao carregar lojas', 'error'); }
        })();
    }, [subdomain, notify]);

    const loadKeyterms = useCallback(async () => {
        if (!subdomain) return;
        setLoading(true);
        try {
            const r = await apiService.keytermsList({ subdomain, store_id: storeId || undefined });
            setItems(r.items || []);
            setDefaults(r.defaults || []);
        } catch (e) {
            notify(e.response?.data?.detail || e.message || 'Erro ao carregar keyterms', 'error');
        } finally {
            setLoading(false);
        }
    }, [subdomain, storeId, notify]);

    // Termos ao trocar empresa/loja
    useEffect(() => { loadKeyterms(); }, [loadKeyterms]);

    const openNewForm = () => { setForm(emptyForm); setFormOpen(true); };
    const openEditForm = (row) => { setForm({ id: row.id, term: row.term }); setFormOpen(true); };
    const closeForm = () => { setFormOpen(false); setForm(emptyForm); };

    const canSave = (form.term || '').trim().length > 0;

    const save = async () => {
        if (!canSave) { notify('Digite o termo', 'error'); return; }
        if (savingRef.current) return;
        savingRef.current = true;
        setSaving(true);
        try {
            await apiService.keytermsSave({
                id: form.id || undefined,
                subdomain,
                store_id: storeId ? Number(storeId) : null,
                term: form.term.trim(),
            });
            notify(form.id ? 'Termo atualizado' : 'Termo adicionado', 'success');
            closeForm();
            loadKeyterms();
        } catch (e) {
            notify(e.response?.data?.detail || e.message || 'Erro ao salvar termo', 'error');
        } finally {
            savingRef.current = false;
            setSaving(false);
        }
    };

    const remove = async (row) => {
        if (!(await confirmar(`Excluir o termo "${row.term}"?`, { titulo: 'Excluir termo', perigo: true, rotuloConfirmar: 'Excluir' }))) return;
        try {
            await apiService.keytermsDelete({ subdomain, id: row.id });
            notify('Termo excluído', 'success');
            loadKeyterms();
        } catch (e) {
            notify(e.response?.data?.detail || e.message || 'Erro ao excluir termo', 'error');
        }
    };

    const scopeLabel = storeId
        ? (stores.find((s) => String(s.id) === String(storeId))?.display_name || `Loja ${storeId}`)
        : 'Padrão da empresa (todas as lojas)';

    return (
        <div className="kt-page">
            {toast && <div className={`kt-toast kt-toast-${toast.type}`}>{toast.msg}</div>}

            <h1 className="kt-title">🔤 Palavras-chave (Keyterms)</h1>
            <p className="kt-help">
                Termos enviados à ElevenLabs na transcrição. O <strong>padrão da empresa</strong> vale para todas as lojas;
                termos de uma <strong>loja</strong> se somam ao padrão para aquela loja.
            </p>

            <div className="kt-filters">
                <div className="kt-field">
                    <label>Empresa</label>
                    <select value={subdomain} onChange={(e) => setSubdomain(e.target.value)}>
                        <option value="">— Selecione —</option>
                        {companies.map((c) => <option key={c.subdomain} value={c.subdomain}>{c.name}</option>)}
                    </select>
                </div>
                <div className="kt-field">
                    <label>Escopo</label>
                    <select value={storeId} onChange={(e) => setStoreId(e.target.value)} disabled={!subdomain}>
                        <option value="">Padrão da empresa (todas as lojas)</option>
                        {stores.map((s) => <option key={s.id} value={s.id}>{s.display_name || s.name}</option>)}
                    </select>
                </div>
                <div className="kt-field kt-filters-actions">
                    <button className="kt-btn kt-btn-primary" onClick={openNewForm} disabled={!subdomain}>
                        ➕ Novo termo
                    </button>
                </div>
            </div>

            {formOpen && (
                <div className="kt-form-panel">
                    <h2>{form.id ? 'Editar termo' : 'Novo termo'} — {scopeLabel}</h2>
                    <div className="kt-form-row">
                        <div className="kt-field kt-field-grow">
                            <label>Termo *</label>
                            <input
                                type="text" maxLength={128} value={form.term} autoFocus
                                placeholder="ex.: Madeira Originals"
                                onChange={(e) => setForm((p) => ({ ...p, term: e.target.value }))}
                                onKeyDown={(e) => { if (e.key === 'Enter' && canSave) save(); }}
                            />
                        </div>
                        <div className="kt-form-actions">
                            <button className="kt-btn" onClick={closeForm} disabled={saving}>Cancelar</button>
                            <button className="kt-btn kt-btn-primary" onClick={save} disabled={saving || !canSave}>
                                {saving ? 'Salvando…' : 'Salvar'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="kt-list">
                <h2>Termos — {scopeLabel} {loading ? '(carregando…)' : `(${items.length})`}</h2>
                {!subdomain && <p className="kt-empty">Selecione uma empresa para ver os termos.</p>}
                {subdomain && !loading && items.length === 0 && <p className="kt-empty">Nenhum termo cadastrado neste escopo.</p>}
                {items.length > 0 && (
                    <table className="kt-table">
                        <thead>
                            <tr><th>Termo</th><th></th></tr>
                        </thead>
                        <tbody>
                            {items.map((row) => (
                                <tr key={row.id}>
                                    <td>{row.term}</td>
                                    <td className="kt-actions">
                                        <button className="kt-btn kt-btn-sm" onClick={() => openEditForm(row)}>✏️ Editar</button>
                                        <button className="kt-btn kt-btn-sm kt-btn-danger" onClick={() => remove(row)}>🗑️ Excluir</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Quando uma loja está selecionada, mostra os termos padrão da empresa (também se aplicam, somente leitura) */}
            {storeId && defaults.length > 0 && (
                <div className="kt-list kt-defaults">
                    <h2>Padrão da empresa (também aplicado nesta loja)</h2>
                    <div className="kt-chips">
                        {defaults.map((row) => <span key={row.id} className="kt-chip">{row.term}</span>)}
                    </div>
                </div>
            )}
        </div>
    );
}

export default KeytermsConfig;
